/**
 * MatterItemDetail — the ONE editor behind the detail pane.
 *
 * Every list in the matter workspace — questions, tasks, suggestions, notes —
 * opens the same docked container and this component decides what goes in it.
 * The tabs themselves are read-only tables; every mutation lives here.
 *
 * That split is the point of the rebuild. Before, each tab carried its own
 * modal or global side panel, so "edit this thing" looked and behaved
 * differently three times and always covered the list it came from.
 *
 * Items are resolved from the datasets `MatterDetails` already holds, by
 * STABLE id — never by array index, which is what let a background refresh
 * silently move an open editor onto a different row.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { MatterSelection } from '../MatterSurfaceContext';
import NoteDetail, { DetailNote, NoteType } from './NoteDetail';
import QuestionDetail, { DetailQuestion } from './QuestionDetail';
import TaskDetail from './TaskDetail';
import { taskId } from '../../tasks/taskConstants';

interface Props {
  selection: MatterSelection | null;
  matterUuid: string;
  canEdit: boolean;
  notes: any[];
  questionnaire: any[];
  matterTasks: any[];
  /** Local, not yet accepted — keyed by the id the Tasks tab assigned. */
  suggestions: Array<{ id: string; task: any }>;
  /** Matter collaborators, for the assignee picker. */
  collaborators: any[];
  onUpdate: () => void;
  /** Drop a suggestion from the tab's local list. */
  /** Returns a promise so the accept flow can await the persisted removal. */
  onDismissSuggestion?: (id: string) => void | Promise<void>;
  onClose: () => void;
}

/** A note's stable id is its label — that is the key the API addresses. */
export const noteId = (note: any): string => String(note?.label ?? '');

/**
 * A question's stable id.
 *
 * `question_id` is the key every questionnaire endpoint takes. Older rows can
 * arrive without one, so the question text is the fallback — still stable
 * across a refresh, which an array index never was.
 */
export const questionId = (question: any): string =>
  String(question?.question_id ?? question?.question ?? '');

const MatterItemDetail: React.FC<Props> = ({
  selection,
  matterUuid,
  canEdit,
  notes,
  questionnaire,
  matterTasks,
  suggestions,
  collaborators,
  onUpdate,
  onDismissSuggestion,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!selection) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <p className="text-xs text-fg-muted text-center">
          {t('Select a row to see it here.')}
        </p>
      </div>
    );
  }

  if (selection.kind === 'note') {
    const note = selection.mode === 'create'
      ? null
      : (notes.find((n) => noteId(n) === selection.id) as DetailNote | undefined);

    // The row went away underneath the editor — a delete elsewhere, or a
    // refresh that dropped it. Say so rather than rendering an empty form
    // that would silently create a new record on save.
    if (selection.mode === 'edit' && !note) {
      return (
        <div className="h-full flex items-center justify-center p-6">
          <p className="text-xs text-fg-muted text-center">
            {t('That note is no longer here.')}
          </p>
        </div>
      );
    }

    return (
      <NoteDetail
        key={`note:${selection.id ?? 'new'}`}
        matterUuid={matterUuid}
        note={note ? { label: note.label, value: String(note.value ?? ''), type: note.type as NoteType } : null}
        canEdit={canEdit}
        onSaved={onUpdate}
        onClose={onClose}
      />
    );
  }

  if (selection.kind === 'qa') {
    const question = selection.mode === 'create'
      ? null
      : (questionnaire.find((q) => questionId(q) === selection.id) as DetailQuestion | undefined);

    if (selection.mode === 'edit' && !question) {
      return (
        <div className="h-full flex items-center justify-center p-6">
          <p className="text-xs text-fg-muted text-center">
            {t('That question is no longer here.')}
          </p>
        </div>
      );
    }

    return (
      <QuestionDetail
        key={`qa:${selection.id ?? 'new'}`}
        matterUuid={matterUuid}
        question={question ?? null}
        canEdit={canEdit}
        onSaved={onUpdate}
        onClose={onClose}
      />
    );
  }

  if (selection.kind === 'task' || selection.kind === 'suggestion') {
    const isSuggestion = selection.kind === 'suggestion';
    const found = selection.mode === 'create'
      ? null
      : isSuggestion
        ? suggestions.find((s) => s.id === selection.id)?.task
        : matterTasks.find((task) => taskId(task) === selection.id);

    if (selection.mode === 'edit' && !found) {
      return (
        <div className="h-full flex items-center justify-center p-6">
          <p className="text-xs text-fg-muted text-center">
            {isSuggestion
              ? t('That suggestion is no longer here.')
              : t('That task is no longer here.')}
          </p>
        </div>
      );
    }

    return (
      <TaskDetail
        key={`${selection.kind}:${selection.id ?? 'new'}`}
        matterUuid={matterUuid}
        task={found ?? null}
        isSuggestion={isSuggestion}
        canEdit={canEdit}
        matterCollaborators={collaborators}
        onSaved={onUpdate}
        onDismissSuggestion={
          isSuggestion && selection.id
            ? () => onDismissSuggestion?.(selection.id as string)  // returns the promise — do not drop it
            : undefined
        }
        onClose={onClose}
      />
    );
  }

  return (
    <div className="h-full flex items-center justify-center p-6">
      <p className="text-xs text-fg-muted text-center">{t('Not available yet.')}</p>
    </div>
  );
};

export default MatterItemDetail;
